import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { RouteTableTypeEnum } from 'src/app/samwebDescription/samwin/RouteTableTypeEnum';
import { UserActionSettingChanged } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/toolbar-settings/toolbar-settings.actions';
import { ToolbarSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/toolbar-settings/toolbar-settings.state';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { ReQueueActionSettingRemoteDto } from 'src/generated/signalR/re-queue-action-setting-remote-dto';
import { QueueShortInfoRemoteDto, UsersService } from 'src/generated/swagger';
import { ActionSpecificSettingsComponent } from '../action-specific-settings-component';

@Component({
   selector: 'samweb-requeue-action-settings',
   templateUrl: './requeue-action-settings.component.html',
   styleUrls: ['./requeue-action-settings.component.scss'],
})
export class RequeueActionSettingsComponent extends ActionSpecificSettingsComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();
   public queues$: BehaviorSubject<QueueShortInfoRemoteDto[]>;
   public searchTextValue$: BehaviorSubject<string>;

   public actionSetting$: Observable<ReQueueActionSettingRemoteDto>;
   public filteredSelectableQueues$: Observable<QueueShortInfoRemoteDto[]>;
   public selectedQueue$: Observable<QueueShortInfoRemoteDto>;

   private openRequeueDialog = <QueueShortInfoRemoteDto>{
      id: SkillConstants.OPEN_REQUEUE_DIALOG,
      name: this.translateService.instant('user_actions.types.requeue_call.settings.open_requeue_dialog'),
      queueTypeId: null,
      routeType: null,
   };

   constructor(private store: Store, private translateService: TranslateService, private usersService: UsersService) {
      super();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit(): void {
      this.queues$ = new BehaviorSubject<QueueShortInfoRemoteDto[]>([]);
      this.searchTextValue$ = new BehaviorSubject<string>('');

      this.actionSetting$ = this.store.select(ToolbarSettingsState.getActionSettingFn).pipe(
         map((fn) => fn(this.brickInstanceId, this.toolbarId, this.actionId)),
         map((x) => x as ReQueueActionSettingRemoteDto),
         map((x) => _.cloneDeep(x))
      );

      this.filteredSelectableQueues$ = combineLatest([this.queues$, this.searchTextValue$]).pipe(
         map(([queues, searchTextValue]) => queues.filter((s) => s.name?.toLowerCase().indexOf(searchTextValue.toLowerCase()) !== -1))
      );

      this.selectedQueue$ = combineLatest([this.actionSetting$, this.queues$]).pipe(
         switchMap(([settings, skills]) => {
            const selectedValue = _.find(skills, (skill) => {
               if (skill.id === settings.skillId) {
                  return skill;
               }
            }) as QueueShortInfoRemoteDto;
            if (_.isNil(selectedValue)) {
               return of(_.first(skills));
            }
            return of(selectedValue);
         })
      );

      this.usersService
         .getRequeueTargetsForCalls()
         .pipe(map((x) => x.queues))
         .pipe(
            map((response: QueueShortInfoRemoteDto[]) =>
               _(response)
                  .filter((x) => x.routeType !== RouteTableTypeEnum.MESSAGES.value)
                  .orderBy((x) => x.name.toLowerCase())
                  .value()
            ),
            tap((response: QueueShortInfoRemoteDto[]) => this.queues$.next([this.openRequeueDialog, ...response] ?? [])),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   callPositionChanged(settings: ReQueueActionSettingRemoteDto, value: number) {
      settings.callPosition = value;
      this.store.dispatch(new UserActionSettingChanged(this.brickInstanceId, this.toolbarId, this.actionId, settings));
   }

   selectQueue(settings: ReQueueActionSettingRemoteDto, value: QueueShortInfoRemoteDto) {
      settings.skillId = value.id;
      this.store.dispatch(new UserActionSettingChanged(this.brickInstanceId, this.toolbarId, this.actionId, settings));
   }

   searchTextChanged(value) {
      this.searchTextValue$.next(value);
   }
}
